import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { Combobox } from "@/components/flexkit/combobox/Combobox";
import { usePrefetchMembersByIds, useListMembersInfinite, getMemberFromCache } from "@/queries/organization-queries";
import {
    useListServiceAccounts,
    getServiceAccountFromCache,
    usePrefetchServiceAccountsByIds,
} from "@/queries/service-account-queries";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState, type FC } from "react";
import { useDebounce } from "use-debounce";

type MemberComboboxProps = {
    selectedMember: string | null;
    onMemberChange: (value: string) => void;
    className?: string;
    disabled?: boolean;
    placeholder?: string;
    /** Include service accounts in the list */
    includeServiceAccounts?: boolean;
};

type MemberItem = {
    id: string;
    name: string;
    avatarUrl?: string;
    type: "member" | "service-account";
};

/**
 * Self-contained member combobox with backend search and infinite scroll.
 * Includes "All members" as a special option.
 */
export const MemberCombobox: FC<MemberComboboxProps> = ({
    selectedMember,
    onMemberChange,
    className,
    disabled = false,
    placeholder = "All members",
    includeServiceAccounts = true,
}) => {
    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue] = useDebounce(searchValue, 250, {
        trailing: true,
    });
    const queryClient = useQueryClient();

    const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isPending, isFetching } = useListMembersInfinite({
        search: debouncedSearchValue,
    });

    const { data: serviceAccountsData, isLoading: isLoadingServiceAccounts } = useListServiceAccounts({
        enabled: includeServiceAccounts,
    });

    // Prefetch the selected member/service account to ensure it's always available in cache
    // This handles the case where searching filters out the currently selected item
    const selectedIds = useMemo(
        () => (selectedMember && selectedMember !== "all" ? [selectedMember] : []),
        [selectedMember],
    );
    // Try both - hooks will skip if ID is already cached or doesn't match
    usePrefetchMembersByIds(selectedIds);
    usePrefetchServiceAccountsByIds(selectedIds, { enabled: includeServiceAccounts });

    const membersData = useMemo(() => {
        if (!data) return { members: [] };
        return {
            members: data.pages.flatMap((page) => page.members),
        };
    }, [data]);

    // Convert members and service accounts to a unified format
    const memberItems = useMemo<MemberItem[]>(() => {
        const items: MemberItem[] = membersData.members.map((m) => ({
            id: m.userId,
            name: m.fullName,
            avatarUrl: m.avatarUrl,
            type: "member" as const,
        }));

        // Add service accounts with client-side filtering (backend search not yet supported)
        if (includeServiceAccounts && serviceAccountsData?.serviceAccounts) {
            const searchLower = debouncedSearchValue.toLowerCase();
            // todo(ft): replace this ugly frontend filtering with backend search (CORE-6922)
            const filteredSAs = debouncedSearchValue
                ? serviceAccountsData.serviceAccounts.filter((sa) => sa.name.toLowerCase().includes(searchLower))
                : serviceAccountsData.serviceAccounts;

            const saItems: MemberItem[] = filteredSAs.map((sa) => ({
                id: sa.id,
                name: sa.name,
                type: "service-account" as const,
            }));
            items.push(...saItems);
        }

        // Sort by name
        items.sort((a, b) => a.name.localeCompare(b.name));

        return items;
    }, [membersData.members, serviceAccountsData?.serviceAccounts, includeServiceAccounts, debouncedSearchValue]);

    const selectedMemberData = useMemo((): MemberItem | undefined => {
        if (!selectedMember || selectedMember === "all") return undefined;

        // Check current list first
        const foundMember = memberItems.find((m) => m.id === selectedMember);
        if (foundMember) return foundMember;

        // Check members data from infinite query
        const member = membersData.members.find((m) => m.userId === selectedMember);
        if (member) return { id: member.userId, name: member.fullName, avatarUrl: member.avatarUrl, type: "member" };

        // Check individual member cache (populated by usePrefetchMembersByIds)
        const cachedMember = getMemberFromCache(queryClient, selectedMember);
        if (cachedMember) {
            return { id: cachedMember.userId, name: cachedMember.fullName, avatarUrl: cachedMember.avatarUrl, type: "member" };
        }

        // Check service accounts
        const sa = serviceAccountsData?.serviceAccounts?.find((s) => s.id === selectedMember);
        if (sa) return { id: sa.id, name: sa.name, type: "service-account" };

        // Check service account cache
        const cachedSa = getServiceAccountFromCache(queryClient, selectedMember);
        if (cachedSa) return { id: cachedSa.id, name: cachedSa.name, type: "service-account" };

        return undefined;
    }, [selectedMember, memberItems, membersData.members, serviceAccountsData?.serviceAccounts, queryClient]);

    const handleScrollEnd = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const membersWithAll = useMemo(() => {
        // Don't show "All members" when searching
        if (searchValue) {
            return memberItems;
        }

        return [
            { id: "all", name: "All members", type: "member" as const },
            ...memberItems,
        ];
    }, [searchValue, memberItems]);

    const isLoading = isPending || (includeServiceAccounts && isLoadingServiceAccounts);

    return (
        <Combobox
            value={selectedMember ?? "all"}
            onValueChange={onMemberChange}
            filterPlaceholder="Search members..."
            disabled={disabled || (memberItems.length === 0 && !isLoading)}
            className={className}
            loading={isLoading}
        >
            <Combobox.Value>
                {selectedMember && selectedMemberData ? (
                    <div className="flex items-center gap-2">
                        <Avatar className="shrink-0" size={24}>
                            {selectedMemberData.avatarUrl && <AvatarImage src={selectedMemberData.avatarUrl} />}
                            <AvatarFallback>
                                <AvatarInitials name={selectedMemberData.name} size={24} />
                            </AvatarFallback>
                        </Avatar>
                        <Combobox.ValueLabel>{selectedMemberData.name}</Combobox.ValueLabel>
                    </div>
                ) : (
                    <Combobox.ValueLabel>{placeholder}</Combobox.ValueLabel>
                )}
            </Combobox.Value>
            <Combobox.Popover className="z-[999] w-80 max-w-[calc(100vw-1rem)]" sameWidth={false}>
                <Combobox.SearchBox onValueChanged={setSearchValue} loading={isFetching || isFetchingNextPage} />
                <Combobox.List
                    onScrollEnd={handleScrollEnd}
                    scrollEndThreshold={1000}
                    items={membersWithAll}
                    searchKeys={[]}
                    disableFiltering
                    noMatchesComponent={<Combobox.Empty>No members found</Combobox.Empty>}
                >
                    {(item) => (
                        <Combobox.ListItem key={item.id} value={item.id}>
                            {item.id !== "all" && (
                                <Combobox.ListItemLeadingIcon>
                                    <Avatar className="h-5 w-5" size={24}>
                                        {item.avatarUrl && <AvatarImage src={item.avatarUrl} />}
                                        <AvatarFallback>
                                            <AvatarInitials name={item.name} size={24} />
                                        </AvatarFallback>
                                    </Avatar>
                                </Combobox.ListItemLeadingIcon>
                            )}
                            <Combobox.ListItemTitle>{item.name}</Combobox.ListItemTitle>
                        </Combobox.ListItem>
                    )}
                </Combobox.List>
            </Combobox.Popover>
        </Combobox>
    );
};
